import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { Notification } from '@/types/notification';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

export type NotificationPageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export interface NotificationsResponse {
  data: Notification[];
  total: number;
}

export class NotificationService {
  static readonly DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE;
  static readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS;

  // Get notifications for a user with server-side pagination
  static async getAllNotifications(
    _userId: string,
    page: number = 1,
    limit: number = DEFAULT_PAGE_SIZE
  ): Promise<NotificationsResponse> {
    const response = await api.get<{ success: boolean; data: Notification[]; total: number }>(
      '/notifications',
      { params: { page, limit } }
    );
    // API client returns axios response.data, so response is { success, data, total }
    const data = Array.isArray(response?.data) ? response.data : [];
    const total = typeof (response as { total?: number }).total === 'number' ? (response as { total: number }).total : data.length;
    return { data, total };
  }

  // Mark a notification as read
  static async markAsRead(notificationId: number, userId: string): Promise<{ status: string; message: string }> {
    const response = await api.post<{ success: boolean; data: Notification }>(`/notifications/${notificationId}`);
    return {
      status: 'success',
      message: 'Notifikation markerad som läst',
    };
  }

  // Delete a notification
  static async deleteNotification(notificationId: number, userId: string): Promise<{ status: string; message: string }> {
    await api.delete<{ success: boolean; message: string }>(`/notifications/${notificationId}`);
    return {
      status: 'success',
      message: 'Notifikation raderad framgångsrikt',
    };
  }

  // Check if user has unread notifications
  static async hasUnreadNotifications(userId: string): Promise<boolean> {
    const response = await api.get<{ success: boolean; data: boolean }>('/notifications/has-unread');
    return response.data || false;
  }

  // Mark all notifications as read
  static async markAllAsRead(userId: string): Promise<{ status: string; message: string }> {
    await api.post<{ success: boolean; data: { count: number } }>('/notifications/mark-all-as-read');
    return {
      status: 'success',
      message: 'Alla notifikationer markerade som lästa',
    };
  }

  // Delete all notifications
  static async deleteAllNotifications(userId: string): Promise<{ status: string; message: string; data: { count: number } }> {
    const response = await api.delete<{ success: boolean; data: { count: number }; message: string }>('/notifications/all');
    // API client unwraps response.data, so response is already { success: boolean; data: { count: number }; message: string }
    return {
      status: 'success',
      message: 'Alla notifikationer raderade framgångsrikt',
      data: (response as { data: { count: number } }).data || { count: 0 },
    };
  }
}
